"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';

const navLinks = [
  { label: 'Inicio', href: '#inicio' },
  { label: 'Nosotros', href: '#sobre-nosotros' },
  { label: 'Servicios', href: '#servicios' },
  { label: 'Proceso', href: '#proceso' },
  { label: 'Contacto', href: '#contacto' },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrolled
            ? 'rgba(10, 15, 26, 0.95)'
            : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(7,205,225,0.1)' : '1px solid transparent',
          boxShadow: scrolled ? '0 4px 30px rgba(0,0,0,0.3)' : 'none',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link href="/home-page" className="flex items-center gap-3 group">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all group-hover:shadow-glow-cyan"
              style={{
                background: 'linear-gradient(135deg, rgba(7,205,225,0.2), rgba(7,205,225,0.05))',
                border: '1px solid rgba(7,205,225,0.3)',
              }}
            >
              {/* Circuit icon */}
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="#07CDE1" strokeWidth="1.5">
                <rect x="2" y="8" width="4" height="8" rx="1" />
                <rect x="18" y="8" width="4" height="8" rx="1" />
                <rect x="8" y="2" width="8" height="4" rx="1" />
                <rect x="8" y="18" width="8" height="4" rx="1" />
                <circle cx="12" cy="12" r="2" fill="#07CDE1" />
                <line x1="6" y1="12" x2="10" y2="12" />
                <line x1="14" y1="12" x2="18" y2="12" />
                <line x1="12" y1="6" x2="12" y2="10" />
                <line x1="12" y1="14" x2="12" y2="18" />
              </svg>
            </div>
            <div>
              <span className="font-heading font-700 text-white text-lg tracking-tight leading-none block">
                GTS<span className="text-[#07CDE1]">Consultores</span>
              </span>
              <span className="text-[9px] font-body text-[#8892A4] tracking-[0.15em] uppercase">
                Tecnología a medida
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks?.map((link) => (
              <a
                key={link?.href}
                href={link?.href}
                className="font-body text-[13px] font-500 text-[#8892A4] hover:text-white transition-colors duration-200 relative group"
              >
                {link?.label}
                <span
                  className="absolute -bottom-1 left-0 w-0 h-px bg-[#07CDE1] transition-all duration-300 group-hover:w-full"
                />
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://wa.me/584245756868"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded text-[12px] font-body font-600 transition-all"
              style={{
                background: 'rgba(37,211,102,0.1)',
                border: '1px solid rgba(37,211,102,0.25)',
                color: '#25D366',
              }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current flex-shrink-0">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
            <a
              href="#contacto"
              className="btn-primary px-5 py-2 rounded text-[12px]"
            >
              ¿Trabajamos juntos?
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded"
            aria-label="Toggle menu"
            style={{ border: '1px solid rgba(7,205,225,0.2)' }}
          >
            <span
              className="w-5 h-px bg-[#07CDE1] transition-all duration-300"
              style={{ transform: menuOpen ? 'rotate(45deg) translateY(3.5px)' : 'none' }}
            />
            <span
              className="w-5 h-px bg-[#07CDE1] transition-all duration-300"
              style={{ opacity: menuOpen ? 0 : 1 }}
            />
            <span
              className="w-5 h-px bg-[#07CDE1] transition-all duration-300"
              style={{ transform: menuOpen ? 'rotate(-45deg) translateY(-3.5px)' : 'none' }}
            />
          </button>
        </div>
      </nav>
      {/* Mobile menu overlay */}
      <div
        className="fixed inset-0 z-40 flex flex-col md:hidden transition-all duration-500"
        style={{
          background: 'rgba(7, 11, 20, 0.97)',
          backdropFilter: 'blur(20px)',
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? 'all' : 'none',
          transform: menuOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        <div className="flex items-center justify-between px-6 h-20 border-b border-[rgba(7,205,225,0.1)]">
          <span className="font-heading font-700 text-white text-lg">
            GTS<span className="text-[#07CDE1]">Consultores</span>
          </span>
          <button
            onClick={closeMenu}
            className="w-10 h-10 flex items-center justify-center text-[#8892A4] hover:text-white"
            aria-label="Close menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 flex flex-col justify-center px-8 gap-6">
          {navLinks?.map((link, i) => (
            <a
              key={link?.href}
              href={link?.href}
              onClick={closeMenu}
              className="font-heading font-700 text-white text-3xl tracking-tight hover:text-[#07CDE1] transition-colors"
              style={{ transitionDelay: `${i * 0.05}s` }}
            >
              {link?.label}
            </a>
          ))}
        </nav>

        <div className="px-8 pb-12 space-y-4">
          <a
            href="https://wa.me/584245756868"
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
            className="flex items-center justify-center gap-3 w-full py-4 rounded text-base font-heading font-600"
            style={{
              background: 'rgba(37,211,102,0.1)',
              border: '1px solid rgba(37,211,102,0.3)',
              color: '#25D366',
            }}
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            WhatsApp: +58 424 575 6868
          </a>
          <a
            href="#contacto"
            onClick={closeMenu}
            className="btn-primary flex items-center justify-center gap-2 w-full py-4 rounded text-base"
          >
            ¿Trabajamos juntos?
          </a>
        </div>
      </div>
    </>
  );
}